// src/actions/messages_actions.js
import {
  fetchThreads,
  fetchThreadMessages,
  createOrFetchMyThread,
  sendThreadMessage,
  markThreadRead,
  markThreadUnread,
  reactToMessageApi,
  createOrFetchMasterThreadWithStudent,
} from "../util/messages_api_util";

export const RECEIVE_MESSAGE_THREADS = "RECEIVE_MESSAGE_THREADS";
export const RECEIVE_CURRENT_THREAD = "RECEIVE_CURRENT_THREAD";
export const RECEIVE_THREAD_MESSAGES = "RECEIVE_THREAD_MESSAGES";
export const RECEIVE_MESSAGE = "RECEIVE_MESSAGE";
export const SET_ACTIVE_THREAD = "SET_ACTIVE_THREAD";
export const CLEAR_THREAD_UNREAD = "CLEAR_THREAD_UNREAD";
export const SET_THREAD_UNREAD = "SET_THREAD_UNREAD";

export const INCREMENT_THREAD_UNREAD = "INCREMENT_THREAD_UNREAD";

export const INCREMENT_STUDENT_UNREAD = "INCREMENT_STUDENT_UNREAD";
export const CLEAR_STUDENT_UNREAD = "CLEAR_STUDENT_UNREAD";

// 🔹 allow us to set the unread count from the server
export const SET_STUDENT_UNREAD = "SET_STUDENT_UNREAD";

export const RECEIVE_MESSAGE_REACTION = "RECEIVE_MESSAGE_REACTION";

// ========== BASIC ACTION CREATORS ==========

export const receiveMessageReaction = (message) => ({
  type: RECEIVE_MESSAGE_REACTION,
  message,
});

export const receiveMessageThreads = (threads) => ({
  type: RECEIVE_MESSAGE_THREADS,
  threads,
});

export const receiveCurrentThread = (thread) => ({
  type: RECEIVE_CURRENT_THREAD,
  thread,
});

export const receiveThreadMessages = (threadId, messages) => ({
  type: RECEIVE_THREAD_MESSAGES,
  threadId,
  messages,
});

export const receiveMessage = (message) => ({
  type: RECEIVE_MESSAGE,
  message,
});

export const setActiveThread = (threadId) => ({
  type: SET_ACTIVE_THREAD,
  threadId,
});

export const clearThreadUnread = (threadId) => ({
  type: CLEAR_THREAD_UNREAD,
  threadId,
});

export const setThreadUnread = (threadId, count = 1) => ({
  type: SET_THREAD_UNREAD,
  threadId,
  count,
});

export const incrementThreadUnread = (threadId) => ({
  type: INCREMENT_THREAD_UNREAD,
  threadId,
});

export const incrementStudentUnread = () => ({
  type: INCREMENT_STUDENT_UNREAD,
});

export const clearStudentUnread = () => ({
  type: CLEAR_STUDENT_UNREAD,
});

export const setStudentUnread = (count) => ({
  type: SET_STUDENT_UNREAD,
  count,
});

// ========== THUNKS ==========

// Master: load all of their threads
export const getMessageThreads = () => async (dispatch) => {
  const threads = await fetchThreads();

  // ✅ session expired -> util returns null -> do nothing
  if (threads === null) return;

  dispatch(receiveMessageThreads(Array.isArray(threads) ? threads : []));
};

// Master or student: load messages for a single thread
export const getMessagesForThread = (threadId) => async (dispatch) => {
  if (!threadId) return;

  const messages = await fetchThreadMessages(threadId);

  // ✅ session expired -> util returns null -> do nothing
  if (messages === null) return;

  dispatch(receiveThreadMessages(threadId, Array.isArray(messages) ? messages : []));
};

// Student: get or create their own single thread, set active, and load messages
export const getMyThreadAndMessages = () => async (dispatch, getState) => {
  const thread = await createOrFetchMyThread();

  // ✅ session expired -> util returns null -> do nothing
  if (!thread || !thread._id) return;

  dispatch(receiveCurrentThread(thread));
  dispatch(setActiveThread(thread._id));

  // hydrate unread counter from server for the STUDENT view
  const state = getState();
  const currentUser = state.session.user;

  if (currentUser && currentUser.role === "student") {
    const serverCount =
      typeof thread.unreadCountStudent === "number"
        ? thread.unreadCountStudent
        : typeof thread.unreadCount === "number"
        ? thread.unreadCount
        : 0;

    dispatch(setStudentUnread(serverCount));
  }

  const messages = await fetchThreadMessages(thread._id);

  // ✅ session expired -> util returns null -> do nothing
  if (messages === null) return;

  dispatch(receiveThreadMessages(thread._id, Array.isArray(messages) ? messages : []));
};

// Send a message in a thread
export const sendMessage =
  (threadId, text, mediaUrl = null, mediaType = null) =>
  async (dispatch) => {
    if (!threadId) return;

    const message = await sendThreadMessage(threadId, text, mediaUrl, mediaType);

    // ✅ session expired -> util returns null -> do nothing
    if (!message) return;

    dispatch(receiveMessage(message));
  };

// Mark all messages in a thread as read for the current user
export const markThreadMessagesRead =
  (threadId) => async (dispatch, getState) => {
    const state = getState();
    const currentUser = state.session.user;
    if (!currentUser || !threadId) return;

    const res = await markThreadRead(threadId);

    // ✅ session expired -> util returns null -> do nothing
    if (res === null) return;

    // Master / grandmaster side: clear badge on thread list
    dispatch(clearThreadUnread(threadId));

    // Student side: clear their single-thread unread count
    if (currentUser.role === "student") {
      dispatch(clearStudentUnread());
    }
  };

// Master: mark thread as unread (leave green dot)
export const markThreadMessagesUnread =
  (threadId) => async (dispatch) => {
    if (!threadId) return;
    const res = await markThreadUnread(threadId);
    if (res === null) return;
    const forced = !!res?.forcedUnread;
    dispatch(setThreadUnread(threadId, forced ? 1 : 0));
    return res;
  };

// React to a single message (emoji)
export const reactToMessage = (messageId, emoji) => async (dispatch) => {
  if (!messageId) return;

  const updatedMessage = await reactToMessageApi(messageId, emoji);

  // ✅ session expired -> util returns null -> do nothing
  if (!updatedMessage) return;

  dispatch(receiveMessageReaction(updatedMessage));
};

// Master / grandmaster: create (or fetch) a thread with a specific student
export const createThreadWithStudent =
  (studentId) => async (dispatch, getState) => {
    if (!studentId) return null;

    const thread = await createOrFetchMasterThreadWithStudent(studentId);

    // ✅ session expired -> util returns null -> do nothing
    if (!thread || !thread._id) return null;

    // Merge into threads list so unread dots & sidebar see it immediately
    const existingThreads = getState().messages.threads || [];
    const idx = existingThreads.findIndex((t) => String(t._id) === String(thread._id));

    let nextThreads;
    if (idx === -1) {
      nextThreads = [...existingThreads, thread];
    } else {
      nextThreads = [...existingThreads];
      nextThreads[idx] = thread;
    }

    dispatch(receiveMessageThreads(nextThreads));
    dispatch(receiveCurrentThread(thread));
    dispatch(setActiveThread(thread._id));

    return thread;
  };
